import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Save, Trash2, Upload, FileText, MessageSquarePlus, Paperclip, ListChecks, FileSignature, Download, Plus, X, Send, Mail } from "lucide-react";
import { STATI, STATO_LABEL, STATO_BADGE } from "@/pages/Sinistri";
import { DateInputIT } from "@/components/DateInputIT";
import { SinistroIncaricati } from "@/components/sinistri/SinistroIncaricati";
import { AlertTriangle, ArrowRightLeft } from "lucide-react";

export default function SinistroDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [compagnie, setCompagnie] = useState([]);
  const [nota, setNota] = useState("");
  const [uploading, setUploading] = useState(false);
  const [newChk, setNewChk] = useState("");
  const [templates, setTemplates] = useState([]);
  const [tpl, setTpl] = useState("");
  const [modulo, setModulo] = useState({ oggetto: "", destinatario: "", corpo: "" });
  const [genLoading, setGenLoading] = useState(false);
  const [invio, setInvio] = useState(null);
  const [sending, setSending] = useState(false);

  const load = useCallback(() => { api.get(`/sinistri/${id}`).then(({ data }) => setS(data)); }, [id]);
  useEffect(() => {
    load();
    api.get("/agency/compagnie").then(({ data }) => setCompagnie(data));
    api.get("/sinistri/templates").then(({ data }) => setTemplates(data));
  }, [load]);

  const setF = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      await api.put(`/sinistri/${id}`, { ...s, importo_richiesto: Number(s.importo_richiesto) || 0, importo_liquidato: Number(s.importo_liquidato) || 0 });
      toast.success("Sinistro aggiornato"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const addNota = async () => {
    if (!nota.trim()) return;
    await api.post(`/sinistri/${id}/note`, { testo: nota }); setNota(""); load();
  };
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/sinistri/${id}/documenti`, fd);
      toast.success("Documento caricato"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload non riuscito"); }
    finally { setUploading(false); }
  };
  const delDoc = async (docId) => { await api.delete(`/sinistri/documenti/${docId}`); load(); };
  const delPratica = async () => { if (!window.confirm("Eliminare la pratica di sinistro?")) return; await api.delete(`/sinistri/${id}`); navigate("/gestione-sinistri"); };

  // Checklist
  const toggleChk = async (item) => { await api.patch(`/sinistri/${id}/checklist/${item.item_id}`, { presente: !item.presente }); load(); };
  const addChk = async () => { if (!newChk.trim()) return; await api.post(`/sinistri/${id}/checklist`, { nome: newChk, obbligatorio: false }); setNewChk(""); load(); };
  const delChk = async (itemId) => { await api.delete(`/sinistri/${id}/checklist/${itemId}`); load(); };

  // Moduli
  const selectTpl = async (tid) => {
    setTpl(tid);
    try {
      const { data } = await api.post(`/sinistri/${id}/genera-modulo/preview?template_id=${tid}`);
      setModulo(data);
    } catch { toast.error("Errore nel caricamento del modulo"); }
  };
  const scaricaModulo = async (allega) => {
    setGenLoading(true);
    try {
      const { data } = await api.post(`/sinistri/${id}/genera-modulo`, { ...modulo, allega }, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = `Modulo_${s.numero_pratica}.pdf`; a.click(); URL.revokeObjectURL(url);
      toast.success(allega ? "PDF generato e allegato alla pratica" : "PDF scaricato");
      if (allega) load();
    } catch { toast.error("Errore nella generazione"); }
    finally { setGenLoading(false); }
  };

  // Invio alla compagnia (simulato)
  const openInvio = async () => {
    const t = templates.find((x) => x.tipo === "apertura_compagnia");
    let pre = { oggetto: "", destinatario: "", corpo: "" };
    if (t) {
      try { const { data } = await api.post(`/sinistri/${id}/genera-modulo/preview?template_id=${t.template_id}`); pre = data; } catch { /* noop */ }
    }
    const comp = compagnie.find((c) => c.nome === s.compagnia);
    setInvio({ email_destinatario: comp?.email_sinistri || "", oggetto: pre.oggetto, destinatario: pre.destinatario, corpo: pre.corpo, doc_ids: (s.documenti || []).map((d) => d.doc_id), nuovo_stato: "INVIATO_COMPAGNIA" });
  };
  const toggleInvioDoc = (docId) => setInvio((iv) => ({ ...iv, doc_ids: iv.doc_ids.includes(docId) ? iv.doc_ids.filter((x) => x !== docId) : [...iv.doc_ids, docId] }));
  const sendInvio = async () => {
    if (!invio.email_destinatario.trim()) { toast.error("Inserisci l'email dell'ufficio sinistri"); return; }
    setSending(true);
    try {
      await api.post(`/sinistri/${id}/invia-compagnia`, invio);
      toast.success("Richiesta apertura preparata e inviata (SIMULATO)");
      setInvio(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nell'invio"); }
    finally { setSending(false); }
  };

  if (!s) return <div className="min-h-screen bg-slate-50"><Header /><div className="p-10 text-center text-slate-400">Caricamento…</div></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <button onClick={() => navigate("/gestione-sinistri")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-6"><ArrowLeft className="h-4 w-4" /> Tutti i sinistri</button>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-heading text-2xl font-medium text-slate-900">{s.numero_pratica}</h1>
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${STATO_BADGE[s.stato]}`}>{STATO_LABEL[s.stato]}</span>
            </div>
            <p className="text-slate-500 text-sm mt-1">{s.cliente_nome}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="text-red-500" onClick={delPratica}><Trash2 className="h-4 w-4 mr-2" /> Elimina</Button>
            <Button variant="outline" className="border-violet-300 text-violet-700 hover:bg-violet-50" onClick={openInvio} data-testid="sin-invia-btn"><Send className="h-4 w-4 mr-2" /> Invia a compagnia</Button>
            <Button onClick={save} className="bg-red-600 hover:bg-red-700 text-white" data-testid="sin-detail-save"><Save className="h-4 w-4 mr-2" /> Salva</Button>
          </div>
        </div>

        {s.in_ritardo && (
          <div className="mb-6 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="sin-ritardo-alert">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>Pratica ferma nello stato <b>«{STATO_LABEL[s.stato]}»</b> da <b>{s.giorni_in_stato} giorni</b> (soglia {s.soglia_giorni}). Verifica con {s.stato === "AL_LEGALE" ? "il legale" : s.stato === "TRATTATIVA_LIQUIDATORE" ? "il liquidatore" : s.stato.includes("PERIZIA") ? "il perito" : "la compagnia"} e aggiorna lo stato.</span>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-sm text-slate-600">Stato</Label>
                <Select value={s.stato} onValueChange={(v) => setF("stato", v)}><SelectTrigger data-testid="sin-detail-stato"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATI.map((x) => <SelectItem key={x} value={x}>{STATO_LABEL[x]}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label className="text-sm text-slate-600">Compagnia</Label>
                <Select value={s.compagnia} onValueChange={(v) => setF("compagnia", v)}><SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{compagnie.map((c) => <SelectItem key={c.compagnia_id} value={c.nome}>{c.nome}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label className="text-sm text-slate-600">Ramo</Label><Input value={s.ramo || ""} onChange={(e) => setF("ramo", e.target.value)} /></div>
              <div className="space-y-1.5"><Label className="text-sm text-slate-600">N° polizza</Label><Input value={s.numero_polizza || ""} onChange={(e) => setF("numero_polizza", e.target.value)} /></div>
              <div className="space-y-1.5"><Label className="text-sm text-slate-600">Data sinistro</Label><DateInputIT value={s.data_sinistro || ""} onChange={(v) => setF("data_sinistro", v)} testid="sin-detail-data" /></div>
              <div className="space-y-1.5"><Label className="text-sm text-slate-600">N° sinistro compagnia</Label><Input value={s.numero_sinistro_compagnia || ""} onChange={(e) => setF("numero_sinistro_compagnia", e.target.value)} /></div>
              <div className="space-y-1.5"><Label className="text-sm text-slate-600">Referente / perito</Label><Input value={s.referente || ""} onChange={(e) => setF("referente", e.target.value)} /></div>
              <div className="space-y-1.5"><Label className="text-sm text-slate-600">Controparte</Label><Input value={s.controparte || ""} onChange={(e) => setF("controparte", e.target.value)} /></div>
              <div className="space-y-1.5"><Label className="text-sm text-slate-600">Importo richiesto (€)</Label><Input type="number" value={s.importo_richiesto || ""} onChange={(e) => setF("importo_richiesto", e.target.value)} /></div>
              <div className="space-y-1.5"><Label className="text-sm text-slate-600">Importo liquidato (€)</Label><Input type="number" value={s.importo_liquidato || ""} onChange={(e) => setF("importo_liquidato", e.target.value)} data-testid="sin-detail-liquidato" /></div>
              <div className="col-span-2 space-y-1.5"><Label className="text-sm text-slate-600">Descrizione</Label><Textarea value={s.descrizione || ""} onChange={(e) => setF("descrizione", e.target.value)} /></div>
            </div>

            <SinistroIncaricati s={s} setF={setF} />

            <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="font-heading text-lg text-slate-900 mb-3 flex items-center gap-2"><Paperclip className="h-4 w-4" /> Documenti</h3>
              <label className="inline-flex items-center gap-2 cursor-pointer text-sm px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors mb-3">
                <Upload className="h-4 w-4" /> {uploading ? "Caricamento…" : "Carica documento (PDF, foto)"}
                <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp" data-testid="sin-doc-upload" onChange={(e) => upload(e.target.files?.[0])} />
              </label>
              <div className="space-y-2" data-testid="sin-doc-list">
                {(s.documenti || []).length === 0 ? <p className="text-sm text-slate-400">Nessun documento.</p>
                  : s.documenti.map((d) => (
                    <div key={d.doc_id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
                      <a href={`${API}/sinistri/documenti/${d.doc_id}/download`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-slate-700 hover:text-red-600 truncate">
                        <FileText className="h-4 w-4 shrink-0" /> <span className="truncate">{d.original_filename}</span>
                      </a>
                      <button onClick={() => delDoc(d.doc_id)} className="text-red-400 hover:text-red-600 shrink-0"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="sin-moduli-card">
              <h3 className="font-heading text-lg text-slate-900 mb-1 flex items-center gap-2"><FileSignature className="h-4 w-4" /> Moduli & comunicazioni</h3>
              <p className="text-sm text-slate-500 mb-3">Genera lettere precompilate (apertura sinistro, sollecito, cliente) su carta intestata.</p>
              <div className="flex gap-2 items-end flex-wrap mb-3">
                <div className="flex-1 min-w-[220px] space-y-1.5">
                  <Label className="text-sm text-slate-600">Modulo</Label>
                  <Select value={tpl} onValueChange={selectTpl}><SelectTrigger data-testid="sin-modulo-select"><SelectValue placeholder="Scegli un modulo…" /></SelectTrigger>
                    <SelectContent>{templates.map((t) => <SelectItem key={t.template_id} value={t.template_id}>{t.nome}</SelectItem>)}</SelectContent></Select>
                </div>
                <Button variant="outline" onClick={() => navigate("/gestione-sinistri/moduli")}>Gestisci modelli</Button>
              </div>
              {tpl && (
                <div className="space-y-3 border-t border-slate-100 pt-3">
                  <div className="space-y-1.5"><Label className="text-sm text-slate-600">Destinatario</Label>
                    <Textarea rows={2} value={modulo.destinatario} onChange={(e) => setModulo((m) => ({ ...m, destinatario: e.target.value }))} data-testid="sin-modulo-destinatario" /></div>
                  <div className="space-y-1.5"><Label className="text-sm text-slate-600">Oggetto</Label>
                    <Input value={modulo.oggetto} onChange={(e) => setModulo((m) => ({ ...m, oggetto: e.target.value }))} data-testid="sin-modulo-oggetto" /></div>
                  <div className="space-y-1.5"><Label className="text-sm text-slate-600">Testo</Label>
                    <Textarea rows={9} value={modulo.corpo} onChange={(e) => setModulo((m) => ({ ...m, corpo: e.target.value }))} data-testid="sin-modulo-corpo" /></div>
                  <div className="flex gap-2 flex-wrap">
                    <Button onClick={() => scaricaModulo(false)} disabled={genLoading} className="bg-red-600 hover:bg-red-700 text-white" data-testid="sin-modulo-download"><Download className="h-4 w-4 mr-2" /> Scarica PDF</Button>
                    <Button variant="outline" onClick={() => scaricaModulo(true)} disabled={genLoading} data-testid="sin-modulo-allega"><Paperclip className="h-4 w-4 mr-2" /> Genera e allega</Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-1 space-y-6">
            <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="sin-checklist-card">
              <h3 className="font-heading text-lg text-slate-900 mb-3 flex items-center gap-2"><ListChecks className="h-4 w-4" /> Checklist documenti</h3>
              <div className="space-y-2 mb-3" data-testid="sin-checklist-list">
                {(s.checklist || []).length === 0 ? <p className="text-sm text-slate-400">Nessuna voce.</p>
                  : s.checklist.map((c) => (
                    <div key={c.item_id} className="flex items-center gap-2 group">
                      <Checkbox checked={c.presente} onCheckedChange={() => toggleChk(c)} data-testid={`sin-chk-${c.item_id}`} />
                      <span className={`text-sm flex-1 ${c.presente ? "line-through text-slate-400" : "text-slate-700"}`}>
                        {c.nome}{c.obbligatorio && <span className="text-red-500 ml-1" title="Obbligatorio">*</span>}
                      </span>
                      <button onClick={() => delChk(c.item_id)} className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
              </div>
              <div className="flex gap-2">
                <Input value={newChk} onChange={(e) => setNewChk(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addChk()} placeholder="Aggiungi voce…" className="h-9 text-sm" data-testid="sin-chk-input" />
                <Button size="sm" variant="outline" onClick={addChk} data-testid="sin-chk-add"><Plus className="h-4 w-4" /></Button>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="font-heading text-lg text-slate-900 mb-3 flex items-center gap-2"><MessageSquarePlus className="h-4 w-4" /> Diario</h3>
              <Textarea value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Aggiungi una nota / comunicazione…" className="mb-2" data-testid="sin-nota-input" />
              <Button size="sm" onClick={addNota} disabled={!nota.trim()} className="w-full mb-4 bg-slate-800 hover:bg-slate-900 text-white" data-testid="sin-nota-add">Aggiungi nota</Button>
              <div className="space-y-3 max-h-[420px] overflow-y-auto" data-testid="sin-note-list">
                {(s.note || []).length === 0 ? <p className="text-sm text-slate-400">Nessuna nota.</p>
                  : s.note.map((n) => (
                    <div key={n.nota_id} className={`border-l-2 pl-3 ${n.tipo === "cambio_stato" ? "border-indigo-300" : "border-slate-200"}`} data-testid={n.tipo === "cambio_stato" ? "sin-nota-cambio-stato" : undefined}>
                      <p className={`text-sm whitespace-pre-wrap ${n.tipo === "cambio_stato" ? "text-indigo-700 font-medium flex items-center gap-1" : "text-slate-700"}`}>{n.tipo === "cambio_stato" && <ArrowRightLeft className="h-3.5 w-3.5 shrink-0" />}{n.testo}</p>
                      <p className="text-[11px] text-slate-400 mt-1">{n.autore} · {new Date(n.created_at).toLocaleString("it-IT")}</p>
                    </div>
                  ))}
              </div>
            </div>

            {(s.invii || []).length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="sin-invii-card">
                <h3 className="font-heading text-lg text-slate-900 mb-3 flex items-center gap-2"><Mail className="h-4 w-4" /> Invii alla compagnia</h3>
                <div className="space-y-3">
                  {s.invii.map((iv) => (
                    <div key={iv.invio_id} className="border-l-2 border-violet-200 pl-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-700 truncate">{iv.email_destinatario}</span>
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">{iv.stato}</span>
                      </div>
                      <p className="text-xs text-slate-500 truncate">{iv.oggetto}</p>
                      <p className="text-[11px] text-slate-400 mt-0.5">{(iv.allegati || []).length} allegati · {new Date(iv.created_at).toLocaleString("it-IT")}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <Dialog open={!!invio} onOpenChange={(o) => !o && setInvio(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Send className="h-5 w-5 text-violet-600" /> Invia richiesta di apertura alla compagnia</DialogTitle>
            <DialogDescription>Genera la richiesta su carta intestata, allega i documenti e invia all'ufficio sinistri. L'invio email è al momento <b>simulato</b> (nessun provider collegato): il PDF viene comunque generato e allegato alla pratica.</DialogDescription>
          </DialogHeader>
          {invio && (
            <div className="space-y-3 py-2">
              <div className="space-y-1.5"><Label className="text-sm text-slate-600">Email ufficio sinistri {s.compagnia ? `(${s.compagnia})` : ""}</Label>
                <Input type="email" value={invio.email_destinatario} onChange={(e) => setInvio((iv) => ({ ...iv, email_destinatario: e.target.value }))} placeholder="sinistri@compagnia.it" data-testid="sin-invio-email" /></div>
              <div className="space-y-1.5"><Label className="text-sm text-slate-600">Oggetto</Label>
                <Input value={invio.oggetto} onChange={(e) => setInvio((iv) => ({ ...iv, oggetto: e.target.value }))} data-testid="sin-invio-oggetto" /></div>
              <div className="space-y-1.5"><Label className="text-sm text-slate-600">Testo della richiesta</Label>
                <Textarea rows={8} value={invio.corpo} onChange={(e) => setInvio((iv) => ({ ...iv, corpo: e.target.value }))} data-testid="sin-invio-corpo" /></div>
              <div className="space-y-1.5">
                <Label className="text-sm text-slate-600">Documenti da allegare</Label>
                {(s.documenti || []).length === 0 ? <p className="text-sm text-slate-400">Nessun documento caricato. Verrà comunque allegata la richiesta PDF generata.</p>
                  : <div className="space-y-1.5 border border-slate-200 rounded-lg p-3">
                      {s.documenti.map((d) => (
                        <label key={d.doc_id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox checked={invio.doc_ids.includes(d.doc_id)} onCheckedChange={() => toggleInvioDoc(d.doc_id)} data-testid={`sin-invio-doc-${d.doc_id}`} />
                          <FileText className="h-4 w-4 text-slate-400" /> <span className="truncate">{d.original_filename}</span>
                        </label>
                      ))}
                    </div>}
              </div>
              <div className="text-xs text-slate-500 bg-violet-50 border border-violet-100 rounded-lg p-2">La pratica passerà allo stato <b>"Inviato a compagnia"</b> e l'email verrà memorizzata per i prossimi invii.</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvio(null)}>Annulla</Button>
            <Button onClick={sendInvio} disabled={sending} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="sin-invio-send"><Send className="h-4 w-4 mr-2" /> {sending ? "Invio…" : "Invia (simulato)"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
